/**
 * Player color helpers — color is DECOUPLED from seat/turn slot.
 *
 * A player has two 1-indexed numbers:
 *   • `slot`      — seat / turn identity (1..maxPlayers). The engine keys players
 *                   by this; it drives turn order and the seat map.
 *   • `colorSlot` — the palette color the player CHOSE (1..9, unique per game),
 *                   independent of seat count. Null only for legacy rows.
 *
 * Rendering must always use the color, falling back to `slot` when colorSlot is
 * absent (legacy games predating the split).
 *
 * Two consumption paths:
 *   1. Components holding a player OBJECT → `playerColorSlot(player)`.
 *   2. In-game components that only know an engine playerId / seat slot (map
 *      fills, event flashes, log lines) → read the seat→color map from
 *      `PlayerColorContext` via `useColorSlot(seatSlot)`.
 */

import React from 'react'
import { clampPlayerSlot, type PlayerSlot } from '@warcarp/ui'
import { PLAYER_COLOR_SLOTS } from '@warcarp/api-types'

/** The palette slot to render for a player object: chosen color, else seat slot. */
export function playerColorSlot(p: { colorSlot?: number | null; slot: number }): PlayerSlot {
  return clampPlayerSlot(p.colorSlot ?? p.slot)
}

/**
 * The color to pre-select when a viewer opens a lobby they have not joined:
 * their default color if it is a palette slot and no player holds it, else
 * nothing (they pick). Nothing is reserved — the server re-checks on join.
 */
export function initialJoinColor(
  players: ReadonlyArray<{ colorSlot?: number | null; slot: number }>,
  preferred: number | null | undefined,
): number | null {
  if (!Number.isInteger(preferred) || preferred! < 1 || preferred! > PLAYER_COLOR_SLOTS.length) return null
  return players.some(p => (p.colorSlot ?? p.slot) === preferred) ? null : preferred!
}

/**
 * seat slot (1..maxPlayers) → chosen color slot (1..9). Empty map = identity
 * (every seat renders its own slot color), which is the correct fallback for
 * standalone MapView usages (map inspector, previews) with no game context.
 */
export type ColorBySlot = Record<number, number>

const PlayerColorContext = React.createContext<ColorBySlot>({})

export function PlayerColorProvider({
  colorBySlot,
  children,
}: {
  colorBySlot: ColorBySlot
  children: React.ReactNode
}) {
  return <PlayerColorContext.Provider value={colorBySlot}>{children}</PlayerColorContext.Provider>
}

/** The raw seat→color map from context (identity fallback when unprovided). */
export function useColorBySlot(): ColorBySlot {
  return React.useContext(PlayerColorContext)
}

/** Resolve a seat slot to its render color slot, honoring the player's choice. */
export function useColorSlot(seatSlot: number): PlayerSlot {
  const map = React.useContext(PlayerColorContext)
  return clampPlayerSlot(map[seatSlot] ?? seatSlot)
}

/** Build a ColorBySlot from a player list (seat slot → colorSlot ?? slot). */
export function buildColorBySlot(
  players: ReadonlyArray<{ slot: number; colorSlot?: number | null }>,
): ColorBySlot {
  const m: ColorBySlot = {}
  for (const p of players) m[p.slot] = p.colorSlot ?? p.slot
  return m
}

/**
 * Style for a PlayerColorChip on a dark surface: a light rim, so the darkest
 * colours (Raven) stay visible. Undefined in light mode (the chip's own rim).
 */
export function playerChipRim(theme: 'light' | 'dark'): { borderColor: string; borderWidth: number } | undefined {
  return theme === 'dark' ? { borderColor: 'rgba(236,228,204,0.55)', borderWidth: 1.5 } : undefined
}
